import { useMemo, useRef, useState } from 'react';
import { useStoreData, useUser } from '@/queries';
import { useStanRouter } from '@/hooks/useStanRouter';
import { DashboardActionCard } from './DashboardActionCard';
import { ProductSelectionView } from './ProductSelectionView';
import { useAskStanley } from '@/features/ask-stanley/hooks';
import { PRODUCT_TYPE_TO_PAGE_TYPE } from '@/features/dashboard/constants/productTypeMap';
import { OnboardingChecklistSidebar } from '../OnboardingChecklist/OnboardingChecklistSidebar';
import { GENERIC_STATUS } from '@/lib/constants';
import { PageStatus } from '@/types/store';
import { useDirectDepositSetupBanner } from '@/hooks/useDirectDepositSetupBanner';
import { useAllOnboardingTasksCompleted } from '@/hooks/useAllOnboardingTasksCompleted';
import { useIntegrationBanners } from '@/features/dashboard/hooks/useIntegrationBanners';
import { StanBanner } from '@/components/core-components/StanBanner';
import IcAlertCircle from '@/assets/icons/ic-alert-circle.svg';
import { cn } from '@/lib/utils/cn';
import { useMediaQuery } from '@/hooks/useMediaQuery';

// Dashboard action card images
import ImgMarketProduct from '@/assets/images/dynamic-dashboard/action-card-market-product.png';
import ImgAddProduct from '@/assets/images/dynamic-dashboard/action-card-add-product.png';
import ImgAskStanley from '@/assets/images/dynamic-dashboard/action-card-ask-stanley.png';
import ImgChooseTheme from '@/assets/images/dynamic-dashboard/action-card-choose-theme.png';

export function DynamicDashboardV8() {
    const { user } = useUser();
    const { push } = useStanRouter();
    const { openChat, sendMessage, createNewChat, isOpen: isAskStanleyOpen } = useAskStanley();
    const [showProductSelection, setShowProductSelection] = useState(false);
    const isProcessingMarketProducts = useRef(false);
    // Use lg breakpoint (1024px) to match when left sidebar switches to mobile view
    const mobile = useMediaQuery('(max-width: 1023px)');
    const { data: storeData } = useStoreData(user?.default_store_id);

    const showOnboardingDashboard = useMemo(() => {
        return user?.['onboarding-dashboard']?.status === GENERIC_STATUS.ACTIVE;
    }, [user]);

    // Check if warning banner should be shown (direct deposit setup banner)
    const { showBanner: showWarningBanner } = useDirectDepositSetupBanner();

    // Check if all onboarding tasks are completed
    const { allTasksCompleted } = useAllOnboardingTasksCompleted();

    // Integration banner hook (currently unused but may be needed for future banner implementation)
    useIntegrationBanners();

    // Show mobile onboarding checklist when:
    // - showOnboardingDashboard: onboarding dashboard feature is active for the user
    // - !allTasksCompleted: user hasn't completed all onboarding tasks yet
    // - mobile: viewport is mobile-sized (below lg breakpoint, 1024px)
    const showMobileOnboardingChecklist = showOnboardingDashboard && !allTasksCompleted;

    // Check if user has products
    const hasProducts = useMemo(() => {
        if (!storeData || storeData.length === 0) return false;
        const store = storeData[0];
        if (!store || !store.pages) return false;
        return store.pages.some(page => page.status === PageStatus.PUBLISHED || page.status === PageStatus.DRAFT);
    }, [storeData]);

    const handleMarketProducts = () => {
        // Prevent double-clicks and multiple rapid clicks
        if (isProcessingMarketProducts.current) {
            return;
        }

        isProcessingMarketProducts.current = true;

        // Create a new chat first, then open it
        createNewChat();

        openChat('dashboard_card_market');

        // Send the message after a brief delay to ensure chat is open and new chat is set as current
        setTimeout(() => {
            sendMessage('Make me a marketing post');
            // Reset the processing flag after a delay to allow future clicks
            setTimeout(() => {
                isProcessingMarketProducts.current = false;
            }, 1000);
        }, 100);
    };

    const handleAddProduct = () => {
        openChat('dashboard_card_product_selection');
        setShowProductSelection(true);
    };

    const handleProductSelect = (productType: string) => {
        const storeId = user?.default_store_id;
        if (!storeId) {
            return;
        }

        const pageType = PRODUCT_TYPE_TO_PAGE_TYPE[productType];
        if (!pageType) {
            return;
        }

        push(`/stores/${storeId}/page/${pageType}/create`);
    };

    const handleBackToDashboard = () => {
        setShowProductSelection(false);
    };

    const handleAskStanley = () => {
        openChat('dashboard_card');
    };

    const handleChooseTheme = () => {
        push('/stores/?tab=design');
    };

    // Show product selection view if active
    if (showProductSelection) {
        return <ProductSelectionView onBack={handleBackToDashboard} onProductSelect={handleProductSelect} />;
    }

    return (
        <>
            {/* Warning banner for Stripe setup */}
            {showWarningBanner && (
                <StanBanner
                    variant='warning'
                    icon={IcAlertCircle}
                    onClick={() => push('/account_settings')}
                    ctaText='Set Up Direct Deposit'
                    className='mb-6'
                >
                    To start selling, set up direct deposit
                </StanBanner>
            )}
            <div className='flex w-full flex-row justify-between'>
                <div className='flex w-full flex-col lg:flex-row lg:gap-50'>
                    <div className='flex w-full min-w-0 flex-col gap-6 md:w-96 md:min-w-80'>
                        {showMobileOnboardingChecklist && mobile && (
                            <div
                                className={cn(
                                    'transition-all duration-300 ease-in-out',
                                    isAskStanleyOpen
                                        ? 'pointer-events-none -translate-y-2 opacity-0'
                                        : 'pointer-events-auto translate-y-0 opacity-100'
                                )}
                            >
                                <OnboardingChecklistSidebar onAddProduct={handleAddProduct} />
                            </div>
                        )}
                        <div className='mb-3 ml-1'>
                            <h2 className='text-md leading-tight font-bold'>
                                <span className='from-brand-dark to-brand-main bg-gradient-to-r bg-clip-text text-transparent'>
                                    Welcome, {user?.full_name.split(' ')[0] || user?.username || 'User'}
                                </span>{' '}
                                <span>👋</span>
                                <br />
                                <span className='from-brand-dark to-brand-main bg-gradient-to-r bg-clip-text text-transparent'>
                                    Let&apos;s get you ready to sell.
                                </span>
                            </h2>
                        </div>

                        {/* Mobile onboarding checklist - appears above action cards */}

                        <div className='flex flex-col gap-3'>
                            {hasProducts ? (
                                <DashboardActionCard
                                    title='Market your products'
                                    subtitle='Make a post to highlight your offer on socials'
                                    image={ImgMarketProduct}
                                    onClick={handleMarketProducts}
                                />
                            ) : (
                                <DashboardActionCard
                                    title='Choose store theme'
                                    subtitle='Customize your store design and bring your brand to life.'
                                    image={ImgChooseTheme}
                                    onClick={handleChooseTheme}
                                />
                            )}

                            <DashboardActionCard
                                title='Add a product'
                                subtitle='Go from idea to product offer in minutes'
                                image={ImgAddProduct}
                                onClick={handleAddProduct}
                            />

                            <DashboardActionCard
                                title='Ask Stanley'
                                subtitle='Your very own AI Creator coach'
                                image={ImgAskStanley}
                                onClick={handleAskStanley}
                            />
                        </div>
                    </div>
                </div>
                {showMobileOnboardingChecklist && !mobile && (
                    <div
                        className={cn(
                            'hidden transition-all duration-300 ease-in-out lg:block',
                            isAskStanleyOpen
                                ? 'pointer-events-none translate-x-4 opacity-0'
                                : 'pointer-events-auto translate-x-0 opacity-100'
                        )}
                    >
                        <OnboardingChecklistSidebar onAddProduct={handleAddProduct} />
                    </div>
                )}
            </div>
        </>
    );
}
